import React, { useCallback, useEffect, useState } from 'react';
import {
  ShieldCheck, FileCheck2, FileX2, Loader2, AlertCircle, Clock, RefreshCw,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';
import { formatDate } from '@/lib/utils';
import PageHeader from '@/components/PageHeader';
import PartnerDocumentsDialog from '@/components/PartnerDocumentsDialog';

const DOC_LABELS = {
  photo_id: 'Photo ID',
  right_to_work: 'Right to work',
  qualification: 'Qualification',
  public_liability: 'Public liability',
  professional_indemnity: 'Prof. indemnity',
  dbs: 'DBS',
  council_licence: 'Council licence',
  hygiene_declaration: 'Hygiene declaration',
};

function StatusPill({ value }) {
  const map = {
    pending: 'bg-amber-100 text-amber-800',
    approved: 'bg-emerald-100 text-emerald-800',
    rejected: 'bg-rose-100 text-rose-700',
    suspended: 'bg-slate-200 text-slate-700',
  };
  return <span className={`text-[10px] uppercase font-semibold tracking-wider px-2 py-0.5 rounded ${map[value] || 'bg-slate-100 text-slate-700'}`}>{value || 'unknown'}</span>;
}

function DocBadge({ ok, label, testId }) {
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded border ${ok ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-slate-50 text-slate-500'}`}
    >
      {ok ? <FileCheck2 className="h-3 w-3" /> : <FileX2 className="h-3 w-3" />}
      {label}
    </span>
  );
}

function SummaryCard({ icon: Icon, label, value, tone = 'slate', testId }) {
  const toneMap = {
    slate: 'text-slate-700',
    amber: 'text-amber-700',
    rose: 'text-rose-700',
    emerald: 'text-emerald-700',
  };
  return (
    <Card className="p-4 border-slate-200" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[11px] uppercase tracking-wider text-slate-400">{label}</p>
          <p className={`text-2xl font-bold mt-1 ${toneMap[tone]}`}>{value ?? '—'}</p>
        </div>
        <Icon className={`h-5 w-5 ${toneMap[tone]}`} />
      </div>
    </Card>
  );
}

export default function Compliance() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [docsPartner, setDocsPartner] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get('/admin/compliance/partners');
      setSummary(data?.summary || null);
      setRows(data?.rows || []);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load compliance data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Compliance"
        subtitle="Partner document checklist, terms acceptance, and insurance expiry overview."
        onRefresh={load}
        loading={loading}
        refreshTestId="compliance-refresh"
      />

      {error && (
        <Card className="p-4 border-rose-200 bg-rose-50 text-rose-700 text-sm flex items-center gap-2" data-testid="compliance-error">
          <AlertCircle className="h-4 w-4" /> {error}
        </Card>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <SummaryCard testId="summary-total-partners" icon={ShieldCheck} label="Total partners" value={summary?.total_partners} tone="slate" />
        <SummaryCard testId="summary-pending-partners" icon={Clock} label="Pending approval" value={summary?.pending_partners} tone="amber" />
        <SummaryCard testId="summary-missing-docs" icon={FileX2} label="Missing documents" value={summary?.partners_missing_docs} tone="rose" />
        <SummaryCard testId="summary-missing-terms" icon={AlertCircle} label="Pending terms acceptance" value={summary?.partners_pending_terms_acceptance} tone="rose" />
        <SummaryCard testId="summary-insurance-expiring" icon={Clock} label="Insurance expiring soon" value={summary?.partners_with_expiring_insurance} tone="amber" />
        <SummaryCard testId="summary-insurance-expired" icon={AlertCircle} label="Insurance expired" value={summary?.partners_with_expired_insurance} tone="rose" />
      </div>

      <Card className="p-0 border-slate-200 overflow-hidden" data-testid="compliance-table">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <p className="text-sm font-semibold text-slate-900">Partner compliance details</p>
          <Button size="sm" variant="outline" onClick={load} data-testid="compliance-reload">
            <RefreshCw className="h-3.5 w-3.5 mr-1" /> Reload
          </Button>
        </div>
        {loading ? (
          <div className="p-8 text-center text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading compliance data...</div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500">No partners yet.</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {rows.map((r) => (
              <div key={r.partner_id} data-testid={`compliance-row-${r.partner_id}`} className="px-4 py-3 flex flex-col md:flex-row md:items-center gap-3">
                <div className="md:w-72 min-w-0 flex-shrink-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-slate-900 truncate">{r.business_name}</p>
                    <StatusPill value={r.status} />
                    {r.verified_badge && <Badge variant="secondary" className="text-[9px] bg-emerald-50 text-emerald-700 border border-emerald-200">verified</Badge>}
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">{r.category} · {r.base_postcode} · joined {formatDate(r.created_at)}</p>
                </div>
                <div className="flex-1 flex flex-wrap gap-1.5">
                  {Object.entries(DOC_LABELS).map(([key, label]) => (
                    <DocBadge key={key} ok={!!r.documents?.[key]} label={label} testId={`docbadge-${r.partner_id}-${key}`} />
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <Badge
                    variant="outline"
                    className={r.partner_terms_accepted ? 'border-emerald-200 text-emerald-700 bg-emerald-50' : 'border-rose-200 text-rose-700 bg-rose-50'}
                    data-testid={`terms-badge-${r.partner_id}`}
                  >
                    {r.partner_terms_accepted ? 'Terms accepted' : 'Terms pending'}
                  </Badge>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setDocsPartner({ id: r.partner_id, business_name: r.business_name })}
                    data-testid={`view-docs-${r.partner_id}`}
                  >
                    View documents
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <PartnerDocumentsDialog
        open={!!docsPartner}
        onOpenChange={(open) => !open && setDocsPartner(null)}
        partner={docsPartner}
      />
    </div>
  );
}
